import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Loader2 } from 'lucide-react';
import { api } from '../../api/client';
import { useCanWriteCompanySettings } from '../../hooks/useCanWriteCompanySettings';
import { useAuth } from '../../contexts/AuthContext';
import ServicePoliciesPanel from '../../components/equipment/ServicePoliciesPanel';
import { canWriteServicePolicies } from '../../lib/service-policies';
import { canReadPreuse } from '../../lib/preuse';
import { unwrapArray } from '../../lib/api-envelope';
import {
  compareEquipmentTypes,
  toEquipmentType,
  type EquipmentType,
} from '../../types/equipment-type';
import { Tooltip } from '../../components/ui/tooltip';

/**
 * The per-tenant equipment catalog — the types offered by the Add Equipment
 * modal on `EquipmentScreen`, and the grouping the equipment list uses.
 *
 * Modelled on `ServicesPage`, which manages the sibling `service_types`
 * catalog. Four things differ, and three of them are traps:
 *
 *   - A STANDARD ROW GETS NO CONTROLS AT ALL. `service_types` lets a standard
 *     row be renamed and deactivated and refuses only delete; here the API
 *     checks `is_standard` before it parses the body, so all three answer 403.
 *     Copying the sibling's gate would ship two buttons that fail on click.
 *     The constraint belongs to the whole standard set rather than to each
 *     row, so it is stated once above the list instead of ten times inside it.
 *   - THE LIST ASKS FOR DEACTIVATED ROWS. `GET /equipment-types` is active-only
 *     by default, and a management page that cannot see what it deactivated
 *     has made deactivate a one-way door.
 *   - RENAMING HERE IS A MIGRATION, unlike the sibling. API 0334 installed
 *     `trg_equipment_types_cascade_name`, so a rename rewrites `equipment.type`
 *     on every row that references the type. Nothing to do client-side beyond
 *     letting the equipment query go stale.
 *   - THERE IS NO `display_order` COLUMN, so there is no reorder control. Sort
 *     is standard-first then alphabetical, matching the API and the picker.
 *
 * Cross-vertical: no `hasVertical` guard, here or on the nav entry.
 */

const inputCls = 'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm placeholder-fieldiq-muted focus:outline-none focus:border-fieldiq-accent/50';

/**
 * A 409 from POST or PATCH means the name is taken — but by one of two very
 * different rows, and only one has a fix the user can act on:
 *
 *   plain duplicate     another row already carries this name
 *   deactivated holder  the name belongs to a row THIS PAGE can reactivate
 *
 * The API answers both with 409 and ships no machine-readable code, so telling
 * them apart means matching a substring of a human-readable message that the
 * API could reword at any time without anything here failing. THAT IS WHY THE
 * FALLBACK MATTERS: an unmatched 409 still reads as a plain duplicate, which
 * is the true half of the statement either way. The user loses the shortcut,
 * never the explanation.
 */
const isDeactivatedCollision = (err: any) =>
  err?.response?.status === 409 &&
  /deactivated/i.test(String(err?.response?.data?.error ?? ''));

const messageFor = (err: any, fallback: string) =>
  err?.response?.status === 409
    ? 'An equipment type with that name already exists'
    : err?.response?.data?.error || fallback;

export default function EquipmentTypesPage() {
  const queryClient = useQueryClient();

  /*
   * Who may manage a type, as opposed to who may read the list.
   *
   *   GET    /equipment-types      authMiddleware only — no role gate
   *   POST   /equipment-types      requireRole('admin')
   *   PATCH  /equipment-types/:id  requireRole('admin')
   *   DELETE /equipment-types/:id  requireRole('admin')
   *
   * Non-strict, so the API admits `admin` and, on a tenant with
   * `dispatcher_admin_access` on, a `dispatcher`. This used to test for literal
   * 'admin' and so withheld every control from an elevated dispatcher the API
   * would have accepted. It still excludes `owner`: requireRole matches
   * ['admin'] literally, so an owner 403s — which is also why the nav entry
   * carries no `adminOnly` flag, since that resolves through `isAdminRole`.
   * The LIST is useful to everyone and ungated on the API; only the writes hide.
   */
  const isAdmin = useCanWriteCompanySettings();
  // Service policies have their own gate (FE-5): admin AND office staff write
  // them (POLICY_WRITE_ROLES), unlike the type catalog. The left-on-site days
  // moved to Settings → Maintenance agent (FE-4), a casing tab.
  const { user, hasVertical } = useAuth();
  const canWritePolicies = canWriteServicePolicies(user?.role);
  const [openPolicies, setOpenPolicies] = useState<string | null>(null);

  const [addName, setAddName] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  /** Set when the add field collided with a deactivated row we can offer to revive. */
  const [addCollisionId, setAddCollisionId] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);
  /** What DELETE actually did. The two outcomes look different on screen. */
  const [rowNotice, setRowNotice] = useState<string | null>(null);

  const {
    data: types = [],
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useQuery<EquipmentType[]>({
    // Distinct from the ['equipment-types'] key the Add Equipment dropdown
    // reads. That list must stay active-only; this one must not.
    queryKey: ['equipment-types', 'all'],
    queryFn: () =>
      api.equipmentTypes.list(true).then((r) =>
        unwrapArray<any>(r.data)
          .map(toEquipmentType)
          .filter((t): t is EquipmentType => t !== null)
          .sort(compareEquipmentTypes)
      ),
  });

  /*
   * Invalidate the PREFIX, not this page's exact key. Every write here changes
   * what the Add Equipment dropdown should offer, and a rename changes
   * `equipment.type` on every referencing row by database trigger — so the
   * equipment list is stale too, and it is the screen where a rename is
   * actually visible.
   */
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['equipment-types'] });
    queryClient.invalidateQueries({ queryKey: ['equipment'] });
  };

  const clearRowMessages = () => { setRowError(null); setRowNotice(null); };

  const handleAdd = async () => {
    const name = addName.trim();
    if (!name) return;
    setSaving(true);
    setAddError(null);
    setAddCollisionId(null);
    try {
      await api.equipmentTypes.create({ name });
      setAddName('');
      refresh();
    } catch (err: any) {
      if (isDeactivatedCollision(err)) {
        // The row is in `types` — this page asked for deactivated ones — so the
        // message can carry the fix rather than describing it.
        const holder = types.find(
          (t) => t.name.toLowerCase() === name.toLowerCase() && !t.isActive
        );
        setAddCollisionId(holder?.id ?? null);
        setAddError(`“${name}” already exists but is deactivated.`);
      } else {
        setAddError(messageFor(err, 'Failed to add equipment type'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleRename = async (id: string) => {
    const name = editName.trim();
    if (!name) return;
    setSaving(true);
    setEditError(null);
    try {
      await api.equipmentTypes.update(id, { name });
      setEditId(null);
      refresh();
    } catch (err: any) {
      setEditError(messageFor(err, 'Failed to rename equipment type'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (type: EquipmentType) => {
    clearRowMessages();
    try {
      await api.equipmentTypes.update(type.id, { isActive: !type.isActive });
      refresh();
    } catch (err: any) {
      setRowError(messageFor(err, 'Failed to update equipment type'));
    }
  };

  /** Reactivate the deactivated row the add field collided with. */
  const handleReactivate = async (id: string) => {
    setSaving(true);
    try {
      await api.equipmentTypes.update(id, { isActive: true });
      setAddName('');
      setAddError(null);
      setAddCollisionId(null);
      refresh();
    } catch (err: any) {
      setAddError(messageFor(err, 'Failed to reactivate equipment type'));
    } finally {
      setSaving(false);
    }
  };

  /*
   * DELETE picks its own outcome: it hard-deletes an unreferenced type and
   * deactivates one that equipment still points at. Which one happened is
   * reported on the ENVELOPE as `method`, with no `data` key — so `unwrap()`
   * answers null for both and `res.data.method` is the only signal.
   *
   * Saying which one happened is not decoration. A soft delete leaves the row
   * on screen, greyed, and a user who asked for a removal and got a row that is
   * still there has no way to tell that from a failure.
   */
  const handleDelete = async (type: EquipmentType) => {
    if (type.isStandard) return;
    if (!window.confirm(
      `Remove “${type.name}”? If equipment still uses it, the type is deactivated instead of removed.`
    )) return;
    clearRowMessages();
    try {
      const res = await api.equipmentTypes.delete(type.id);
      setRowNotice(
        res.data?.method === 'soft_delete'
          ? `“${type.name}” is still used by existing equipment, so it was deactivated rather than removed.`
          : `“${type.name}” was removed.`
      );
      refresh();
    } catch (err: any) {
      setRowError(messageFor(err, 'Failed to remove equipment type'));
    }
  };

  return (
    <div className="min-h-full">
      {/* The Operations workspace renders the title and the tab strip. */}
      <div className="px-6 py-4 space-y-3">
        <p className="text-xs text-fieldiq-muted">
          The types offered when adding equipment, and the groups the equipment list is filed under.
          Renaming a type updates every piece of equipment already filed under it.
        </p>

        {/* Stated once, for the whole standard set, rather than as ten
            identical tooltips on ten controls that would always fail. */}
        <p className="text-xs text-fieldiq-muted">
          The ten standard types are shared across FieldIQ and can't be renamed, hidden, or removed.
          Types you add are fully yours.
        </p>

        {!isAdmin && (
          /* Say what happened rather than leaving nothing where controls were.
             Silence alone reads as a catalog nobody is allowed to change. */
          <p className="text-xs text-fieldiq-muted">
            Only an admin can add, rename, or remove equipment types.
          </p>
        )}

        {/* Pre-use forms per equipment type (api#1209). Every vertical; shown to
            the roles the API lets read the template register. */}
        {canReadPreuse(user?.role) && (
          <p className="text-xs text-fieldiq-muted" data-testid="preuse-templates-pointer">
            The pre-use checklist crews fill in for each type is under{' '}
            <Link to="/equipment/preuse-templates" className="font-semibold text-fieldiq-text underline underline-offset-2">
              Pre-use templates
            </Link>.
          </p>
        )}

        {hasVertical?.('casing_running') && (
          <p className="text-xs text-fieldiq-muted" data-testid="maintenance-agent-pointer">
            The left-on-site alert days and whether inspections are tracked are under{' '}
            <Link to="/settings/maintenance-agent" className="font-semibold text-fieldiq-text underline underline-offset-2">
              Maintenance agent
            </Link>.
          </p>
        )}

        {rowError && <p className="text-xs text-red-400">{rowError}</p>}
        {rowNotice && <p className="text-xs text-fieldiq-muted">{rowNotice}</p>}

        {isLoading && (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" />
          </div>
        )}

        {isError && (
          <div className="bg-fieldiq-card border border-amber-500/40 rounded-xl p-4 flex items-center justify-between gap-3">
            <p className="text-sm text-amber-300">Couldn't load equipment types.</p>
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-amber-500/50 text-amber-300 hover:text-white disabled:opacity-50"
            >
              {isFetching ? 'Retrying…' : 'Retry'}
            </button>
          </div>
        )}

        {!isLoading && !isError && types.length === 0 && (
          <div className="text-center py-12 text-fieldiq-muted text-sm">No equipment types yet</div>
        )}

        {types.map((type) => (
          <div
            key={type.id}
            className={`bg-fieldiq-card border border-fieldiq-border rounded-xl p-4 ${type.isActive ? '' : 'opacity-50'}`}
          >
          <div className="flex items-center gap-3">
            {editId === type.id ? (
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <input
                    value={editName}
                    onChange={(e) => { setEditName(e.target.value); setEditError(null); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleRename(type.id); }}
                    className={`${inputCls} flex-1 ${editError ? 'border-red-500' : ''}`}
                    placeholder="Type name"
                    aria-label="Equipment type name"
                    autoFocus
                  />
                  <button
                    onClick={() => handleRename(type.id)}
                    disabled={saving || !editName.trim()}
                    className="px-3 py-1.5 text-xs font-semibold bg-fieldiq-accent text-white rounded-lg disabled:opacity-50"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => { setEditId(null); setEditError(null); }}
                    className="px-3 py-1.5 text-xs text-fieldiq-text"
                  >
                    Cancel
                  </button>
                </div>
                {editError && <p className="text-xs text-red-400 mt-1.5">{editError}</p>}
              </div>
            ) : (
              <>
                <span className="text-sm font-medium text-white flex-1">{type.name}</span>
                {!type.isActive && (
                  <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-fieldiq-border text-fieldiq-muted">
                    Deactivated
                  </span>
                )}
                {type.isStandard && (
                  <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-fieldiq-border text-fieldiq-muted">
                    Standard
                  </span>
                )}
                {/* A standard row carries no control of any kind: the API
                    refuses rename, deactivate and delete alike, checking the
                    flag before it reads the body. And a non-admin gets no
                    control either — every write here is admin-only. */}
                {isAdmin && !type.isStandard && (
                  <>
                    {/* Every control carries the type name, matching the
                        delete button below and the sibling page's move-up.
                        Ten rows of identically-named buttons is a real
                        problem for a screen reader and it is also what makes
                        a row addressable without walking the DOM. */}
                    <button
                      onClick={() => { setEditId(type.id); setEditName(type.name); setEditError(null); }}
                      className="px-2 py-1 text-xs text-fieldiq-accent hover:text-white"
                      aria-label={`Rename ${type.name}`}
                    >
                      Rename
                    </button>
                    <button
                      onClick={() => handleToggle(type)}
                      className={`px-2 py-1 text-xs ${type.isActive ? 'text-amber-400' : 'text-green-400'}`}
                      aria-label={`${type.isActive ? 'Deactivate' : 'Activate'} ${type.name}`}
                    >
                      {type.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    <Tooltip content="Remove equipment type">
                      <button
                        onClick={() => handleDelete(type)}
                        className="px-2 py-1 text-xs text-red-400 hover:text-red-300"
                        aria-label={`Remove ${type.name}`}
                      >
                        ×
                      </button>
                    </Tooltip>
                  </>
                )}
                <button
                  onClick={() => setOpenPolicies(openPolicies === type.id ? null : type.id)}
                  aria-expanded={openPolicies === type.id}
                  aria-label={`Service & inspection for ${type.name}`}
                  className="px-2 py-1 text-xs text-fieldiq-text hover:text-white"
                >
                  Service &amp; inspection
                </button>
              </>
            )}
          </div>
          {openPolicies === type.id && (
            <ServicePoliciesPanel typeId={type.id} typeName={type.name} canWrite={canWritePolicies} />
          )}
          </div>
        ))}

        {/* Add new — admin only, matching POST /equipment-types. */}
        {isAdmin && (
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4">
            <p className="text-sm font-medium text-white mb-3">Add Equipment Type</p>
            <div className="flex items-center gap-2">
              <input
                value={addName}
                onChange={(e) => { setAddName(e.target.value); setAddError(null); setAddCollisionId(null); }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
                className={`${inputCls} flex-1 ${addError ? 'border-red-500' : ''}`}
                placeholder="Type name"
                aria-label="New equipment type name"
              />
              <button
                onClick={handleAdd}
                disabled={saving || !addName.trim()}
                className="px-4 py-2 text-xs font-semibold bg-fieldiq-accent text-white rounded-lg disabled:opacity-50 flex items-center gap-1"
              >
                <Plus size={14} /> Add
              </button>
            </div>
            {addError && (
              <p className="text-xs text-red-400 mt-1.5">
                {addError}
                {addCollisionId && (
                  <>
                    {' '}
                    <button
                      onClick={() => handleReactivate(addCollisionId)}
                      disabled={saving}
                      className="underline text-fieldiq-accent hover:text-white disabled:opacity-50"
                    >
                      Reactivate it
                    </button>
                  </>
                )}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
